import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Link,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { resendSignUp } from "data/mutations-common";
import { Form, Formik, FormikConfig } from "formik";
import { useMutation } from "@tanstack/react-query";
import * as Routes from "routes";
import { InferType, object, string } from "yup";

export const ResendVerificationValidationSchema = object({
  email: string().email("Enter a valid email").required("Email is required"),
});

interface ResendVerificationValues {
  email: string;
}

export const ResendVerification = () => {
  // React Query mutation for resending verification
  const resendVerificationMutation = useMutation({
      mutationFn: resendSignUp,
    });

  const onSubmit: FormikConfig<
    InferType<typeof ResendVerificationValidationSchema>
  >["onSubmit"] = async ({ email }) => {
    resendVerificationMutation.mutate(email);
  };

  return (
    <>
      <Formik<ResendVerificationValues>
        initialValues={{ email: "" }}
        onSubmit={onSubmit}
        validationSchema={ResendVerificationValidationSchema}
      >
        {({ values: { email }, handleChange, handleBlur, errors, touched }) => (
          <Form>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: "100vw",
              }}
            >
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: "340px",
                  maxWidth: "500px",
                  m: 2,
                }}
              >
                <CardHeader
                  titleTypographyProps={{ variant: "h5" }}
                  title="Resend Verification"
                  subheader={
                    "If you have lost your verification link, we can send another."
                  }
                />

                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: "16px",
                    width: "100%",
                  }}
                >
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email"
                    type="text"
                    value={email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    disabled={resendVerificationMutation.isPending}
                    sx={customStyle}
                    InputProps={{
                      sx: {
                        borderRadius: "8px",
                        height: "40px",
                        display: "flex",
                        justifyContent: "center",
                        alignContent: "center",
                        alignItems: "center",
                      },
                    }}
                    InputLabelProps={{
                      sx: {},
                    }}
                  />

                  {resendVerificationMutation.isPending ? (
                    <Box width="100%" display="flex" justifyContent="center">
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {resendVerificationMutation.isSuccess && (
                        <Alert
                          variant="outlined"
                          severity="success"
                          sx={{
                            borderRadius: "8px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            alignSelf: "stretch",
                          }}
                        >
                          <Typography variant="body2">
                            Verification link has been successfully resent to{" "}
                            {email}.
                          </Typography>
                        </Alert>
                      )}

                      {resendVerificationMutation.isError && (
                        <Alert
                          variant="outlined"
                          severity="error"
                          sx={{
                            borderRadius: "8px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            alignSelf: "stretch",
                          }}
                        >
                          <Typography variant="body2">
                            Could not resend confirmation.
                          </Typography>
                        </Alert>
                      )}

                      <Button
                        variant="contained"
                        type="submit"
                        disabled={resendVerificationMutation.isPending}
                        fullWidth
                        children="Resend"
                        sx={{ borderRadius: "8px", height: "36px" }}
                      />
                      <ButtonGroup
                        variant="text"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          alignSelf: "stretch",
                          borderRadius: "4px",
                          padding: "0px",
                        }}
                        aria-label="Basic button group"
                      >
                        <Button
                          LinkComponent={Link}
                          size="small"
                          color="primary"
                          href={Routes.signUp}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "4px",

                            padding: "4px 5px",
                            gap: "8px",
                            fontSize: "12px",
                          }}
                        >
                          Sign up
                        </Button>

                        <Button
                          LinkComponent={Link}
                          size="small"
                          color="primary"
                          href={Routes.signIn}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "4px",
                            padding: "4px 5px",
                            gap: "8px",
                            fontSize: "12px",
                          }}
                        >
                          Sign In
                        </Button>

                        <Button
                          LinkComponent={Link}
                          size="small"
                          color="primary"
                          href={Routes.resetPassword}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "4px",

                            padding: "4px 5px",
                            gap: "8px",
                            fontSize: "12px",
                          }}
                        >
                          Password Reset
                        </Button>
                      </ButtonGroup>
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Form>
        )}
      </Formik>

      <Box>{/* <Link href="sign-in">Sign in</Link> */}</Box>
    </>
  );
};

const customStyle: SxProps<Theme> = {
  [`& .MuiInputLabel-root `]: {
    top: "-5px",
  },

  [`& .MuiInputLabel-shrink `]: {
    top: "0px",
  },
};
